import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import React, { useEffect, useRef, useState } from "react";
import { observer, Observer } from "mobx-react";
import GlobalStyles from "../../../Resources/Styles";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import { Feather, MaterialIcons } from "@expo/vector-icons";
import { useScreenDimensions, useDevice } from "../../_layout";
import CustomAlert from "../../Components/CustomAlert";
import { useTranslation } from "react-i18next";
import IconEmptyView from "../../Components/IconEmptyView";
import SearchBar from "../../Components/SearchBar";
import Attribute from "../../Firestore/Attribute";
import AddPredefinedAttributeView from "../../Exercise/AddPredefinedAttributeView";
import { useDrag, useDrop } from "react-dnd";
import { runInAction } from "mobx";

const ModifierLibrary = observer(({ coach }) => {
  const { t } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  useEffect(() => {
    coach.setProperty("searchTermAttr", "");
    return () => {
      coach.setProperty("searchTermAttr", "");
    };
  }, [coach]);

  const ModifierLayoutView = observer(() => {
    const [showingDeleteModifiersAlert, setShowingDeleteModifiersAlert] =
      useState(false);
    const [modifierToDelete, setModifierToDelete] = useState("");

    const deleteModifiersAlertTitle =
      "deletePredefinedAttributeAlertTitle-string";
    const deleteModifiersAlertText =
      "deletePredefinedAttributeAlertText-string";

    const [showingAddModifier, setShowingAddModifier] = useState(false);
    const [selectedTitle, setSelectedTitle] = useState("");

    const canDrag = !coach.searchTermAttr;

    const moveModifier = (dragIndex, hoverIndex, filteredGroup) => {
      runInAction(() => {
        if (dragIndex === hoverIndex) return;

        const dragged = filteredGroup[dragIndex];
        const target = filteredGroup[hoverIndex];
        if (!dragged || !target) return;

        const originalOrders = new Map();
        filteredGroup.forEach((att) => {
          originalOrders.set(att.id, att.order);
        });

        const newFiltered = [...filteredGroup];
        newFiltered.splice(dragIndex, 1);
        newFiltered.splice(hoverIndex, 0, dragged);

        newFiltered.forEach((att, index) => {
          const originalIndex = coach.customPredefinedAttributes.findIndex(
            (a) => a.id === att.id
          );
          if (originalIndex !== -1) {
            const newOrder = index;
            const originalOrder = originalOrders.get(att.id);
            coach.customPredefinedAttributes[originalIndex].order = newOrder;
            if (originalOrder !== newOrder) {
              coach.updateCoachTransactionAttributes(att.id);
            }
          }
        });
      });
    };

    const ModifierTypeLayoutView = observer(({ title }) => {
      const filtered = coach
        .getFilteredAttributesArray(title)
        .sort((a, b) => a.order - b.order);

      const ModifierButton = observer(({ modifier, index, filteredGroup }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [isDeleteHovered, setIsDeleteHovered] = useState(false);
        const ref = useRef(null);

        const dndType = `modifier-${title}`;

        const [{ isOver }, drop] = useDrop({
          accept: dndType,
          drop: (item) => {
            if (item.index !== index) {
              moveModifier(item.index, index, filteredGroup);
              coach.startOrResetBatchTimer();
            }
          },
          collect: (monitor) => ({
            isOver: monitor.isOver(),
          }),
        });

        const [{ isDragging }, drag] = useDrag({
          canDrag: () => canDrag,
          type: dndType,
          item: {
            type: dndType,
            id: modifier.id,
            index,
          },
          end: () => {
            coach.startOrResetBatchTimer();
          },
          collect: (monitor) => ({
            isDragging: monitor.isDragging(),
          }),
        });

        drag(drop(ref));

        return (
          <TouchableOpacity
            ref={ref}
            style={{ maxWidth: "100%", alignSelf: "flex-start" }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                styles.AttributeButton,
                {
                  backgroundColor: isDragging
                    ? Colors.bubbleColor
                    : Colors.whiteColor,
                  borderColor: isHovered
                    ? Colors.bubbleColor
                    : Colors.borderColor,
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  minHeight: 33,
                  opacity: isDragging ? 0 : isOver ? 0.4 : 1,
                },
              ]}
            >
              <View
                style={[
                  GlobalStyles.HStack,
                  {
                    alignItems: "center",
                    gap: 6,
                    maxWidth: "100%",
                    flexShrink: 1,
                  },
                ]}
              >
                <MaterialIcons
                  name="drag-indicator"
                  size={12}
                  color={Colors.grayColor}
                  style={{
                    opacity: (isHovered || isMobile) && canDrag ? 1 : 0,
                  }}
                />

                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    key={modifier.id}
                    style={{
                      fontSize: 12,
                      color: Colors.blackColor,
                      flexShrink: 1,
                      flexWrap: "wrap",
                      // RN-web only: helps very long tokens wrap instead of overflowing
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {t(modifier.name)}
                  </Text>
                </View>

                <View
                  style={[
                    GlobalStyles.HStack,
                    { alignItems: "center", gap: 6, flexShrink: 0 },
                  ]}
                >
                  <TouchableOpacity
                    style={{
                      opacity:
                        isHovered || isMobile ? (isDeleteHovered ? 0.6 : 1) : 0,
                    }}
                    onPress={() => {
                      setModifierToDelete(modifier.id);
                      setShowingDeleteModifiersAlert(true);
                    }}
                    onMouseEnter={() => !isMobile && setIsDeleteHovered(true)}
                    onMouseLeave={() => setIsDeleteHovered(false)}
                  >
                    <Feather name="x" size={14} color={Colors.redColor} />
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </TouchableOpacity>
        );
      });

      const AddModifierButton = ({ title }) => {
        const [isHovered, setIsHovered] = useState(false);
        const action = () => {
          if (!coach.isOffline) {
            setSelectedTitle(title);
            setShowingAddModifier(true);
          }
        };
        return (
          <TouchableOpacity
            onPress={action}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                styles.AttributeButton,
                {
                  backgroundColor: Colors.whiteColor,
                  borderColor: isHovered
                    ? Colors.bubbleColor
                    : Colors.borderColor,
                  paddingHorizontal: 12,
                  opacity: 1,
                  height: 33,
                  alignItems: "center",
                  justifyContent: "center",
                },
              ]}
            >
              <Text
                style={{
                  fontSize: 14,
                  color: Colors.grayColor,
                }}
              >
                +
              </Text>
            </View>
          </TouchableOpacity>
        );
      };

      return (
        (coach.searchTermAttr === "" || (filtered && filtered.length > 0)) && (
          <View>
            <View style={[GlobalStyles.HStack, { marginBottom: 2 }]}>
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: Colors.darkgrayColor,
                }}
              >
                {t(title)}
              </Text>
            </View>

            <View style={{ ...styles.AttributeFlex, gap: 6 }}>
              {filtered.map((modifier, index) => (
                <ModifierButton
                  modifier={modifier}
                  index={index}
                  filteredGroup={filtered}
                  key={modifier.id}
                />
              ))}
              <AddModifierButton title={title} />
            </View>
          </View>
        )
      );
    });

    if (!coach.hasMatchingAttributes()) {
      return (
        <IconEmptyView
          iconLibrary={"MaterialCommunityIcons"}
          mainText={"emptyAttributesTitle-string"}
          subText={"noAttributesFoundText-string"}
          iconName={"magnify-plus-outline"}
          marginTop={140}
          marginBottom={16}
        />
      );
    }

    return (
      <View style={{ flex: 1, gap: 16 }}>
        <>
          <ModifierTypeLayoutView title={Attribute.AttributeTitles.equipment} />
          <ModifierTypeLayoutView title={Attribute.AttributeTitles.tempo} />
          <ModifierTypeLayoutView title={Attribute.AttributeTitles.rom} />
          <ModifierTypeLayoutView
            title={Attribute.AttributeTitles.stanceGrip}
          />
          <ModifierTypeLayoutView
            title={Attribute.AttributeTitles.loadAccomodation}
          />
          <ModifierTypeLayoutView
            title={Attribute.AttributeTitles.accessories}
          />
          <ModifierTypeLayoutView title={Attribute.AttributeTitles.other} />
        </>

        {showingDeleteModifiersAlert && (
          <CustomAlert
            isVisible={showingDeleteModifiersAlert}
            onClose={() => setShowingDeleteModifiersAlert(false)}
            title={deleteModifiersAlertTitle}
            text={deleteModifiersAlertText}
            action={() => setShowingDeleteModifiersAlert(false)}
            specialCTA={"delete-string"}
            coach={coach}
            attributeToDelete={modifierToDelete}
          />
        )}

        {showingAddModifier && selectedTitle !== "" && (
          <AddPredefinedAttributeView
            coach={coach}
            isVisible={showingAddModifier}
            onClose={() => setShowingAddModifier(false)}
            title={selectedTitle}
            setTitle={() => setSelectedTitle("")}
          />
        )}
      </View>
    );
  });

  return (
    <View
      style={[
        styles.container,
        { marginTop: screenSize === "small" ? 16 : 24 },
      ]}
    >
      <Observer>
        {() => (
          <>
            <View
              style={[
                GlobalStyles.HStack,
                { marginBottom: 24, maxWidth: "60%" },
              ]}
            >
              <SearchBar
                text={coach.searchTermAttr}
                onChangeText={(text) => coach.setSearchTermAttr(text)}
                placeholder={t("searchAttributes-string")}
                autoFocusOnDesktop={true}
              />
            </View>
            <ModifierLayoutView />
          </>
        )}
      </Observer>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.backgroundColor,
  },

  AttributeFlex: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
  },
  AttributeButton: {
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
});

export default ModifierLibrary;
